import React, { useState } from 'react'
import { TacticMetric } from '@models/index'
import './MetricBadge.css'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8000'

interface MetricBadgeProps {
  metric: TacticMetric
  compact?: boolean
}

/**
 * Resolves a metric image_file path to a serving URL.
 * Handles absolute Windows paths and /DoS/ paths.
 */
function resolveImageUrl(imagePath: string): string {
  const pathParts = imagePath.split(/[\\\/]/)

  // Check for 'assets' folder
  const assetsIndex = pathParts.findIndex(part => part === 'assets')
  if (assetsIndex !== -1 && assetsIndex < pathParts.length - 2) {
    const category = pathParts[assetsIndex + 1]
    const filename = pathParts[assetsIndex + 2]
    return `${API_BASE_URL}/static/assets/${category}/${filename}`
  }

  // Check for 'Metrics' folder directly
  const metricsIndex = pathParts.findIndex(part => part === 'Metrics')
  if (metricsIndex !== -1 && metricsIndex < pathParts.length - 1) {
    const filename = pathParts[metricsIndex + 1]
    return `${API_BASE_URL}/static/assets/Metrics/${filename}`
  }

  // Fallback: use filename
  const filename = pathParts[pathParts.length - 1]
  return `${API_BASE_URL}/static/assets/Metrics/${filename}`
}

export const MetricBadge: React.FC<MetricBadgeProps> = ({ metric, compact = false }) => {
  const [isImageExpanded, setIsImageExpanded] = useState(false)
  const hasImage = !!metric.image_file

  const formulaText = metric.formula
    ? `${metric.name} = ${metric.formula}`
    : metric.name

  const unitText = metric.unit ? ` [${metric.unit}]` : ''

  return (
    <div className={`metric-badge ${compact ? 'compact' : ''}`}>
      <span className="metric-badge-icon" title="KPI Metric">📊</span>
      <span className="metric-badge-text" title={formulaText + unitText}>
        <span className="metric-name">{metric.name}</span>
        {metric.formula && (
          <span className="metric-formula"> = {metric.formula}</span>
        )}
        {metric.unit && (
          <span className="metric-unit"> [{metric.unit}]</span>
        )}
      </span>
      {hasImage && (
        <button
          className={`metric-image-toggle ${isImageExpanded ? 'expanded' : ''}`}
          onClick={(e) => {
            e.stopPropagation()
            setIsImageExpanded(!isImageExpanded)
          }}
          title={isImageExpanded ? 'Hide metric visual' : 'Show metric visual'}
        >
          🖼️
        </button>
      )}
      {hasImage && isImageExpanded && (
        <div className="metric-image-panel">
          <img
            src={resolveImageUrl(metric.image_file!)}
            alt={metric.name}
            loading="lazy"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none'
            }}
          />
        </div>
      )}
    </div>
  )
}

interface MetricBadgeListProps {
  metrics: TacticMetric[]
  compact?: boolean
  maxDisplay?: number
  startExpanded?: boolean
}

export const MetricBadgeList: React.FC<MetricBadgeListProps> = ({
  metrics,
  compact = false,
  maxDisplay = 5,
  startExpanded = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(startExpanded)

  if (!metrics || metrics.length === 0) return null

  // Collapsed: show compact summary badge
  if (!isExpanded) {
    const names = metrics.slice(0, 3).map(m => m.name)
    const preview = names.join(', ') + (metrics.length > 3 ? '...' : '')
    return (
      <div className="metric-badge-list">
        <button
          className="metric-summary-toggle"
          onClick={() => setIsExpanded(true)}
          title={`${metrics.length} KPIs: ${metrics.map(m => m.name).join(', ')}`}
        >
          <span className="metric-badge-icon">📊</span>
          <span className="metric-summary-count">{metrics.length} KPI{metrics.length !== 1 ? 's' : ''}</span>
          <span className="metric-summary-preview">{preview}</span>
          <span className="metric-summary-expand">▶</span>
        </button>
      </div>
    )
  }

  // Expanded: show full metric badges
  const displayed = metrics.slice(0, maxDisplay)
  const hasMore = metrics.length > maxDisplay

  return (
    <div className="metric-badge-list expanded">
      <button
        className="metric-summary-toggle active"
        onClick={() => setIsExpanded(false)}
      >
        <span className="metric-badge-icon">📊</span>
        <span className="metric-summary-count">{metrics.length} KPI{metrics.length !== 1 ? 's' : ''}</span>
        <span className="metric-summary-expand">▼</span>
      </button>
      {displayed.map((m) => (
        <MetricBadge key={m.id} metric={m} compact={compact} />
      ))}
      {hasMore && (
        <span className="metric-badge-overflow">
          +{metrics.length - maxDisplay} more
        </span>
      )}
    </div>
  )
}
